import { useState, useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Calendar, Clock, List, Eye, Plus, X, Sparkles } from "lucide-react";
import checkPermission from "../../utils/permission";
import TabPage, { TabItemProps } from "../../components/dynamic/Tab";
import { LIST_TOOLBAR_BTN, LIST_TOOLBAR_ICON, LIST_TOOLBAR_ROW } from "../../constants/listToolbarButtons";
import CalendarView from "./CalendarView";
import { ApiService } from "../../services/api";
import { useModal } from "../../hooks/useModal";
import Modal from "../../components/ui/Modal";
import ViewEventModal from "./components/ViewEventModal";
import EventFormModal from "./components/EventFormModal";

interface CalendarEvent {
  id: string;
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  isAllDay: boolean;
  isSystem?: boolean;
  metadata?: any;
  createdAt: string;
  updatedAt: string;
}

interface EventForm {
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  isAllDay: boolean;
}

export default function CalendarPage() {
  const location = useLocation();
  const { showAlert, showConfirm } = useModal();
  const canListCalendars = checkPermission("calendar_list");
  const canAddCalendar = checkPermission("calendar_add");
  const canUpdateCalendar = checkPermission("calendar_update");
  const canDeleteCalendar = checkPermission("calendar_delete");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [combinedEvents, setCombinedEvents] = useState<CalendarEvent[]>([]);
  const [form, setForm] = useState<EventForm>({
    title: "",
    description: "",
    startDate: "",
    endDate: "",
    isAllDay: false,
  });

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setCombinedEvents([]);
    } catch (err: any) {
      await showAlert({
        title: "Hata",
        message: "Etkinlikler yüklenemedi.",
        type: "error",
      });
      console.error("Calendar fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {}, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    if (tab === "combined-events") {
    }
  }, [location.search]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("tr-TR", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString("tr-TR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleCreateEvent = async (initialDate?: string) => {
    if (!canAddCalendar) {
      await showAlert({
        title: "Yetki Hatası",
        message: "Etkinlik oluşturma yetkiniz bulunmamaktadır.",
        type: "warning",
      });
      return;
    }
    const toLocalDateTimeValue = (d: Date) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      const hour = String(d.getHours()).padStart(2, "0");
      const minute = String(d.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day}T${hour}:${minute}`;
    };

    const baseDate = initialDate ? new Date(`${initialDate}T09:00:00`) : new Date();
    const endDate = initialDate ? new Date(`${initialDate}T10:00:00`) : new Date(Date.now() + 60 * 60 * 1000);

    setForm({
      title: "",
      description: "",
      startDate: toLocalDateTimeValue(baseDate),
      endDate: toLocalDateTimeValue(endDate),
      isAllDay: false,
    });
    setShowCreateModal(true);
  };

  const handleViewEvent = (event: CalendarEvent) => {
    setSelectedEvent(event);
    setShowViewModal(true);
  };

  const handleEditEvent = async (event: CalendarEvent) => {
    if (!canUpdateCalendar) {
      await showAlert({
        title: "Yetki Hatası",
        message: "Etkinlik güncelleme yetkiniz bulunmamaktadır.",
        type: "warning",
      });
      return;
    }
    setForm({
      title: event.title,
      description: event.description,
      startDate: new Date(event.startDate).toISOString().slice(0, 16),
      endDate: new Date(event.endDate).toISOString().slice(0, 16),
      isAllDay: event.isAllDay,
    });
    setSelectedEvent(event);
    setShowEditModal(true);
  };

  const handleDeleteEvent = async (event?: CalendarEvent) => {
    const eventToDelete = event || selectedEvent;
    if (!eventToDelete) return;
    if (!canDeleteCalendar) {
      await showAlert({
        title: "Yetki Hatası",
        message: "Etkinlik silme yetkiniz bulunmamaktadır.",
        type: "warning",
      });
      return;
    }

    const confirmed = await showConfirm({
      title: "Etkinliği Sil",
      message: "Bu etkinliği silmek istediğinizden emin misiniz?",
      confirmText: "Sil",
      confirmButtonVariant: "danger",
    });

    if (!confirmed) return;

    try {
      setLoading(true);
      await ApiService.deleteCalendar(eventToDelete.id);
      await showAlert({
        title: "Başarılı",
        message: "Etkinlik silindi!",
        type: "success",
      });
      setShowViewModal(false);
      await fetchEvents();
      window.location.reload();
    } catch (err: any) {
      await showAlert({
        title: "Hata",
        message: err.message || "Etkinlik silinemedi.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title || !form.startDate || !form.endDate) {
      await showAlert({
        title: "Uyarı",
        message: "Lütfen zorunlu alanları doldurun.",
        type: "warning",
      });
      return;
    }

    try {
      setLoading(true);
      const toISOStringLocal = (value: string) => {
        const [datePart, timePart] = value.split("T");
        const [y, m, d] = datePart.split("-").map((x) => parseInt(x, 10));
        const [hh, mm] = timePart.split(":").map((x) => parseInt(x, 10));
        const dt = new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0, 0);
        return dt.toISOString();
      };

      const eventData = {
        title: form.title,
        description: form.description,
        startDate: toISOStringLocal(form.startDate),
        endDate: toISOStringLocal(form.endDate),
        isAllDay: form.isAllDay,
      };

      if (showCreateModal) {
        if (!canAddCalendar) {
          await showAlert({
            title: "Yetki Hatası",
            message: "Etkinlik oluşturma yetkiniz bulunmamaktadır.",
            type: "warning",
          });
          return;
        }
        await ApiService.createCalendar(eventData);
        await showAlert({
          title: "Başarılı",
          message: "Etkinlik oluşturuldu!",
          type: "success",
        });
        setShowCreateModal(false);
        await fetchEvents();
        window.location.reload();
      } else if (showEditModal && selectedEvent) {
        if (!canUpdateCalendar) {
          await showAlert({
            title: "Yetki Hatası",
            message: "Etkinlik güncelleme yetkiniz bulunmamaktadır.",
            type: "warning",
          });
          return;
        }
        await ApiService.updateCalendar(selectedEvent.id, eventData);
        await showAlert({
          title: "Başarılı",
          message: "Etkinlik güncellendi!",
          type: "success",
        });
        setShowEditModal(false);
        await fetchEvents();
        window.location.reload();
      }
    } catch (err: any) {
      await showAlert({
        title: "Hata",
        message: err.message || "Etkinlik kaydedilemedi.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === "checkbox") {
      const checked = (e.target as HTMLInputElement).checked;
      setForm((prev) => ({ ...prev, [name]: checked }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleAIPrompt = () => {
    window.dispatchEvent(new CustomEvent("toggleAIPanel"));
    setTimeout(() => {
      const message = "Takvim etkinliklerim neler?";
      window.dispatchEvent(new CustomEvent("sendAIMessage", { detail: { message } }));
    }, 300);
  };

  const calendarActions = useMemo(
    () => (
      <div className={LIST_TOOLBAR_ROW}>
        {canAddCalendar && (
          <button
            onClick={() => handleCreateEvent()}
            className={`${LIST_TOOLBAR_BTN} bg-green-600 text-white hover:bg-green-700 border-green-600`}>
            <Plus className={LIST_TOOLBAR_ICON} />
            <span>Yeni Etkinlik</span>
          </button>
        )}
        <button
          onClick={handleAIPrompt}
          type="button"
          className={`${LIST_TOOLBAR_BTN} bg-purple-600 text-white hover:bg-purple-700 border-purple-600`}>
          <Sparkles className={LIST_TOOLBAR_ICON} />
          <span>AI'ya Sor</span>
        </button>
      </div>
    ),
    [handleCreateEvent, canAddCalendar]
  );

  const homeworkCalendarTab: TabItemProps = useMemo(
    () => ({
      type: "custom",
      title: "Ödev Takvimi",
      path: "calendar-homework",
      actions: calendarActions,
      isSearch: true,
      showToggle: false,
      customComponent: (
        <CalendarView
          filterReferenceType="HOMEWORK"
          onEditEvent={handleEditEvent}
          onDeleteEvent={handleDeleteEvent}
          onCreateEvent={handleCreateEvent}
          onRefresh={fetchEvents}
          showCreateButton={false}
        />
      ),
    }),
    [calendarActions, handleEditEvent, handleDeleteEvent, handleCreateEvent, fetchEvents]
  );

  const meetCalendarTab: TabItemProps = useMemo(
    () => ({
      type: "custom",
      title: "Toplantı Takvimi",
      path: "calendar-meet",
      actions: calendarActions,
      isSearch: true,
      showToggle: false,
      customComponent: (
        <CalendarView
          filterReferenceType="MEET"
          onEditEvent={handleEditEvent}
          onDeleteEvent={handleDeleteEvent}
          onCreateEvent={handleCreateEvent}
          onRefresh={fetchEvents}
          showCreateButton={false}
        />
      ),
    }),
    [calendarActions, handleEditEvent, handleDeleteEvent, handleCreateEvent, fetchEvents]
  );

  const appointmentCalendarTab: TabItemProps = useMemo(
    () => ({
      type: "custom",
      title: "Randevu Takvimi",
      path: "calendar-appointment",
      actions: calendarActions,
      isSearch: true,
      showToggle: false,
      customComponent: (
        <CalendarView
          filterReferenceType="APPOINTMENT"
          onEditEvent={handleEditEvent}
          onDeleteEvent={handleDeleteEvent}
          onCreateEvent={handleCreateEvent}
          onRefresh={fetchEvents}
          showCreateButton={false}
        />
      ),
    }),
    [calendarActions, handleEditEvent, handleDeleteEvent, handleCreateEvent, fetchEvents]
  );

  const customEventsTab: TabItemProps = useMemo(
    () => ({
      type: "custom",
      title: "Etkinlik Takvimi",
      path: "custom-events",
      actions: calendarActions,
      isSearch: true,
      showToggle: false,
      customComponent: (
        <CalendarView
          filterReferenceType="CUSTOM"
          onEditEvent={handleEditEvent}
          onDeleteEvent={handleDeleteEvent}
          onCreateEvent={handleCreateEvent}
          onRefresh={fetchEvents}
          showCreateButton={true}
        />
      ),
    }),
    [calendarActions, handleEditEvent, handleDeleteEvent, handleCreateEvent, fetchEvents]
  );

  const eventsListTabOld: TabItemProps = useMemo(
    () => ({
      type: "custom",
      title: "Tüm Etkinlikler (Eski)",
      path: "combined-events-old",
      actions: calendarActions,
      isSearch: true,
      showToggle: false,
      customComponent: (
        <div className="px-6 py-6 w-full h-full overflow-auto">
          {loading && <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">Yükleniyor...</div>}

          {combinedEvents.length === 0 && !loading ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Henüz etkinlik bulunamadı.</p>
            </div>
          ) : (
            <div className="space-y-3 pb-4">
              {combinedEvents.map((event) => {
                const isPast = new Date(event.endDate) < new Date();
                return (
                  <div
                    key={event.id + (event as any)?.metadata?.type}
                    className={`rounded-lg p-4 transition-shadow border ${
                      isPast ? "bg-gray-50 border-gray-200 text-gray-500" : "bg-white border-gray-200 hover:shadow-md"
                    }`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-gray-900">{event.title}</h3>
                          {(event as any)?.metadata?.type === "meeting" && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              Toplantı
                            </span>
                          )}
                        </div>
                        {event.description && <p className="text-gray-600 text-sm mb-3 line-clamp-2">{event.description}</p>}
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            <span>{formatDate(event.startDate)}</span>
                          </div>
                          {!event.isAllDay && (
                            <div className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              <span>
                                {formatTime(event.startDate)} - {formatTime(event.endDate)}
                              </span>
                            </div>
                          )}
                          {event.isAllDay && <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">Tüm Gün</span>}
                          {(event as any)?.metadata?.type === "meeting" && (
                            <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full">Toplantı</span>
                          )}
                          {isPast && <span className="bg-gray-200 text-gray-700 text-xs px-2 py-1 rounded-full">Bitti</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <button
                          onClick={() => handleViewEvent(event)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium px-3 py-1 rounded hover:bg-blue-50 transition-colors">
                          Görüntüle
                        </button>
                        {(event as any)?.metadata?.type !== "meeting" && (
                          <>
                            {canUpdateCalendar && !event.isSystem && (
                              <button
                                onClick={() => handleEditEvent(event)}
                                className="text-blue-600 hover:text-blue-800 text-sm font-medium px-3 py-1 rounded hover:bg-blue-50 transition-colors">
                                Düzenle
                              </button>
                            )}
                            {canDeleteCalendar && !event.isSystem && (
                              <button
                                onClick={async () => {
                                  setSelectedEvent(event);
                                  await handleDeleteEvent();
                                }}
                                disabled={loading}
                                className="text-red-600 hover:text-red-800 text-sm font-medium px-3 py-1 rounded hover:bg-red-50 transition-colors disabled:opacity-50">
                                Sil
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ),
    }),
    [calendarActions, combinedEvents, loading, error, canUpdateCalendar, canDeleteCalendar]
  );

  const tabs = useMemo(() => {
    const list: TabItemProps[] = [homeworkCalendarTab, meetCalendarTab, appointmentCalendarTab];
    if (canListCalendars) list.push(customEventsTab);
    return list;
  }, [homeworkCalendarTab, meetCalendarTab, appointmentCalendarTab, customEventsTab, canListCalendars]);

  return (
    <>
      <TabPage title="Takvim" tabs={tabs} />

      <ViewEventModal
        isOpen={showViewModal}
        event={selectedEvent}
        canDelete={!!(canDeleteCalendar && selectedEvent && !selectedEvent.isSystem)}
        canUpdate={!!(canUpdateCalendar && selectedEvent && !selectedEvent.isSystem)}
        loading={loading}
        onClose={() => setShowViewModal(false)}
        onDelete={() => handleDeleteEvent()}
        onEdit={() => {
          if (selectedEvent) {
            setShowViewModal(false);
            handleEditEvent(selectedEvent);
          }
        }}
        formatDate={formatDate}
        formatTime={formatTime}
      />

      <EventFormModal
        isOpen={showCreateModal}
        title="Yeni Etkinlik Oluştur"
        form={form}
        loading={loading}
        onChange={handleFormChange}
        onSubmit={handleSubmitForm}
        onCancel={() => setShowCreateModal(false)}
        submitLabel="Oluştur"
      />

      <EventFormModal
        isOpen={showEditModal && !!selectedEvent}
        title="Etkinlik Düzenle"
        form={form}
        loading={loading}
        onChange={handleFormChange}
        onSubmit={handleSubmitForm}
        onCancel={() => setShowEditModal(false)}
        submitLabel="Güncelle"
      />
    </>
  );
}
